/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DOMContainer } from 'react-overlays/esm/useWaitForDOMRef'
import { useParams } from 'react-router'
import { useHistory, useLocation } from 'react-router-dom'
import AppliedRule from '../../components/appliedRule'
import Button from '../../components/button'
import CardExamList from '../../components/cardExamList'
import CardPatient from '../../components/cardPatient'
import { useAlertHandler } from '../../hooks/alertHandler'
import { usePatients } from '../../hooks/patients'
import { usePrint } from '../../hooks/print'
import { useProcessExam } from '../../hooks/processExam'
import { RoutesEnum } from '../../utils/routesEnum'
import Default from '../loggedArea/default'
import PatientOverlay from './patientOverlay'
import { Container, ContainerHeader, SaveExamCard } from './style'

const ExamResult: FC = (): ReactElement => {
  const { showSuccess } = useAlertHandler()
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingSaveExam, setIsLoadingSaveExam] = useState(false)
  const [isOpenListPatients, setIsOpenListPatients] = useState(false)
  const [isExamSaved, setIsExamSaved] = useState(false)
  const { extractionId } = useParams<{ extractionId: string }>()
  const { getProcessedExam, result } = useProcessExam()
  const { patientsList, getList, saveExam } = usePatients()
  const { search } = useLocation()
  const { isLoading: isLoadingPrint, print: printElement } = usePrint()

  const query = new URLSearchParams(search)

  const patientId = query.get('patientId') as string | undefined

  useEffect(() => {
    document.body.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (patientsList.length === 0) {
      getList()
    }
  }, [patientsList, getList])

  useEffect(() => {
    setIsLoading(true)
    getProcessedExam(extractionId)
      .finally(() => setIsLoading(false))
  }, [extractionId])

  const patientSelected = useMemo(() => {
    return patientsList.find(({ _id }) => _id === patientId)
  }, [patientId, patientsList])

  const save = useCallback(async (patientId: string) => {
    setIsLoadingSaveExam(true)
    try {
      await saveExam({
        patientId,
        extractorKey: extractionId
      })
      setIsExamSaved(true)
      showSuccess('Exame salvo no histórico do paciente')
    } catch {
      //
    }
    setIsLoadingSaveExam(false)
  }, [saveExam])

  const containerHeader = useMemo(() => {
    return (
      <ContainerHeader>
        <CardPatient
          patientId={patientId}
          name={patientSelected?.name}
          imc={result?.patient?.imc}
          age={result?.patient?.age}
          sex={result?.patient?.sex}
          disabled={isExamSaved}
          isLoading={isLoadingSaveExam}
          onSave={() => patientId && save(patientId)}
          onSearch={(e: any) => {
            buttonRef.current = e.target as HTMLButtonElement
            setIsOpenListPatients(true)
          }}
        />
        <div className='exam_info'>
          <span className='label'>Regras analisadas</span>
          <span className='text'>{result?.totalAppliableRules || 0}</span>
          <span className='label'>Regras ativadas</span>
          <span className='text'>{result?.result?.length || 0}</span>
          <div className='button_container'>
            <Button
              block
              to={RoutesEnum.PROCESS_EXAMS}
            >
              PROCESSAR NOVO EXAME
            </Button>
          </div>
        </div>
      </ContainerHeader>
    )
  }, [patientId, result])

  const print = useCallback(async () => {
    const elementsToPrint = (
      <>
        {containerHeader}
        {result?.result?.map(result => (
          <AppliedRule
            key={`result-${result._id}`}
            code={result.code}
            name={result.name}
            content={result.content}
            rules={result.rules}
            calculatedExams={result.calculatedExams}
            type='applied'
          />
        ))}
        <CardExamList exams={result.extracted || []} />
      </>
    )

    await printElement(elementsToPrint)
  }, [result, containerHeader, printElement])

  useEffect(() => {
    function eventHandler (e: any) {
      if (!patientId || isExamSaved === true) {
        return undefined
      }

      const confirmationMessage = `Você tem certeza que deseja sair?
      Ao fechar essa tela você terá que processar o exame novamente.`

      ;(e || window.event).returnValue = confirmationMessage // Gecko + IE
      return confirmationMessage // Gecko + Webkit, Safari, Chrome etc.
    }
    window.addEventListener('beforeunload', eventHandler)

    return () => window.removeEventListener('beforeunload', eventHandler)
  }, [patientId, isExamSaved])

  const buttonRef = useRef<DOMContainer<HTMLElement> | null>(null)

  return (
    <Default
      title='Resultado do Exame'
      navigationComponent={(
        <CardExamList exams={result.extracted || []} />
      )}
      headerComponent={containerHeader}
    >
      <Container>
        {isLoading && 'Carregando...'}
        {result?.result?.map(result => (
          <AppliedRule
            key={`result-${result._id}`}
            code={result.code}
            name={result.name}
            content={result.content}
            rules={result.rules}
            calculatedExams={result.calculatedExams}
            type='applied'
          />
        ))}
      </Container>
      {patientId && (
        <SaveExamCard>
          <Button
            onClick={() => save(patientId)}
            isLoading={isLoadingSaveExam}
            block
            disabled={isExamSaved}
          >
            SALVAR EXAME
          </Button>
          <Button
            onClick={print}
            isLoading={isLoadingPrint}
            block
            variant='link'
            disabled={isLoadingPrint}
          >
            IMPRIMIR EXAME
          </Button>
        </SaveExamCard>
      )}
      {isOpenListPatients && (
        <PatientOverlay
          onHide={() => setIsOpenListPatients(false)}
          onSelect={(id) => {
            setIsOpenListPatients(false)
            save(id)
            history.replace({ search: `?patientId=${id}` })
          }}
          target={buttonRef.current}
        />
      )}
    </Default>
  )
}

export default ExamResult
